import React, { ReactNode, useLayoutEffect, useMemo } from 'react';

import { MAIN_PLAYER_INSTANCE_KEY } from './constants';
import { ASPECT_RATIO_LIST } from '@/modules/common/components/Player/constants/aspectRatio';

import { isInternalType } from '../../helpers/embedCodeParams';
import { getUser } from '../../helpers/user';
import { deleteContext, getContext, setContext, setMainPlayerWidget } from './helpers';
import PlayerWidget from './helpers/LWPlayerWidget';

type Props = {
  children: ReactNode;
  widgetOrigin: string;
  widgetPath: string;
  publisherSlug: string;
  playerName: string;
  autoplay: boolean;
  sound: boolean;
  rtl: boolean;
  enableFloating: boolean;
  offsetTop: number;
  instanceKey: string;
  aspectRatio: (typeof ASPECT_RATIO_LIST)[number];
};

function PlayerProvider({
  children,
  widgetOrigin,
  widgetPath,
  publisherSlug,
  playerName,
  autoplay,
  sound,
  rtl,
  enableFloating,
  offsetTop,
  instanceKey,
  aspectRatio,
}: Props) {
  setContext(instanceKey);

  const player = useMemo(() => {
    const widget = new PlayerWidget({
      playerWidgetUrl: `https://${widgetOrigin}/${widgetPath}`,
      instanceKey,
      publisherSlug,
      playerName,
      autoplay,
      sound,
      rtl,
      offsetTop,
      enableFloating,
      aspectRatio,
    });

    if (isInternalType) {
      widget.setUserID(getUser().id);
    }

    return widget;
  }, [
    widgetOrigin,
    widgetPath,
    instanceKey,
    publisherSlug,
    playerName,
    autoplay,
    sound,
    rtl,
    offsetTop,
    enableFloating,
    aspectRatio,
  ]);

  if (instanceKey === MAIN_PLAYER_INSTANCE_KEY) {
    setMainPlayerWidget(player);
    player.chapteringUIEnable(false);
  }

  useLayoutEffect(
    () => () => {
      player.destroy();
    },
    [
      widgetOrigin,
      widgetPath,
      instanceKey,
      publisherSlug,
      playerName,
      autoplay,
      sound,
      rtl,
      offsetTop,
      enableFloating,
      aspectRatio,
    ],
  );

  useLayoutEffect(
    () => () => {
      deleteContext(instanceKey);
    },
    [],
  );

  const PlayerContext = getContext(instanceKey);

  return <PlayerContext.Provider value={player}>{children}</PlayerContext.Provider>;
}

export default PlayerProvider;
